<template>
  <form autocomplete="off">
    <div class="navless-layout-header">
      <slot name='navbar'>
        <ed-button :to='$routerHistory.previous().path'>
          <i class="fal fa-chevron-left"></i>
          &nbsp;
          Back
        </ed-button>
      </slot>
    </div>
    <div slot="body" :endpoint="`evedpaycards/${cardId}/set-pin`">
      <div class="form">
        <div>
          <p><strong>Change PIN</strong></p>
          <p class="custom-description"><small>Change your 4-digit pin code to be used for transactions on your EvedPay Card</small></p>
        </div>
        <!-- Original PIN input -->
        <div class="label-format">
          <label style="margin-left: 6px">New 4-Digit Pin</label> <br />
          <input v-for="(digit, index) in pin"
                 :key="'pin-' + index"
                 type="password"
                 pattern="[0-9]*"
                 inputmode="numeric"
                 v-model="pin[index]"
                 maxlength="1"
                 :ref="'pinInputField' + index"
                 @input="sanitizeInput(index, pin)"
                 @keydown="handleBackspace(index, pin, $event)"
                 autocomplete="new-password">
        </div>

        <br />

        <!-- Confirm PIN input -->
        <div class="label-format">
          <label style="margin-left: 6px">Confirm New 4-Digit Pin</label> <br />
          <input v-for="(digit, index) in confirmPin"
                 :key="'confirm-pin-' + index"
                 type="password"
                 pattern="[0-9]*"
                 inputmode="numeric"
                 v-model="confirmPin[index]"
                 maxlength="1"
                 :ref="'confirmInputField' + index"
                 @input="sanitizeInputConfirm(index, confirmPin)"
                 @keydown="handleBackspaceConfirm(index, confirmPin, $event)"
                 autocomplete="new-password">
        </div>
        <br />
      </div>
      <div class="button-container">
        <button type="button" class="btn btn-sm custom-button" @click="changePin">UPDATE PIN</button>
      </div>
    </div>
  </form>
</template>

<script>
import { EventBus } from '@/event-bus'
export default {
  alias: 'evedpaychangepin',
  data() {
    return {
      pin: ['', '', '', ''],
      confirmPin: ['', '', '', ''],
      cardId: this.$route.query.cardid,
      cardNumber: this.$route.query.cardNumber
    }
  },
  methods: {
    sanitizeInput(index, pinArray) {
      pinArray[index] = pinArray[index].replace(/\D/g, '')
      this.focusNextInput(index, pinArray)
    },
    sanitizeInputConfirm(index, pinArray) {
      pinArray[index] = pinArray[index].replace(/\D/g, '')
      this.focusNextInputConfirm(index, pinArray)
    },
    focusNextInput(index, pinArray) {
      if (index < pinArray.length - 1 && pinArray[index].trim() !== '') {
        const nextInput = this.$refs['pinInputField' + (index + 1)][0]
        if (nextInput) {
          nextInput.focus()
        }
      }
    },
    focusNextInputConfirm(index, pinArray) {
      if (index < pinArray.length - 1 && pinArray[index].trim() !== '') {
        const nextInput = this.$refs['confirmInputField' + (index + 1)][0]
        if (nextInput) {
          nextInput.focus()
        }
      }
    },
    handleBackspace(index, pinArray, event) {
      if (event.key === 'Backspace' && index > 0 && pinArray[index].trim() === '') {
        const previousInput = this.$refs['pinInputField' + (index - 1)][0]
        if (previousInput) {
          previousInput.focus()
        }
      }
    },
    handleBackspaceConfirm(index, pinArray, event) {
      if (event.key === 'Backspace' && index > 0 && pinArray[index].trim() === '') {
        const previousInput = this.$refs['confirmInputField' + (index - 1)][0]
        if (previousInput) {
          previousInput.focus()
        }
      }
    },
    validatePin(pinValue, confirmPinValue) {
      if (!pinValue) {
        alert('Pin cannot be empty.')
        return true
      }

      if (pinValue !== confirmPinValue) {
        alert("Pins don't match. Please re-enter.")
        this.pin = ['', '', '', '']
        this.confirmPin = ['', '', '', '']
        return true
      }

      if (pinValue.length !== 4) {
        alert('Pin must be 4 digits long. Please re-enter.')
        this.pin = ['', '', '', '']
        this.confirmPin = ['', '', '', '']
        return true
      }

      const numbers = '01234567890'
      const reverseNumbers = '09876543210'
      if (numbers.includes(pinValue) || reverseNumbers.includes(pinValue)) {
        alert('PIN cannot contain digits in sequence.')
        this.pin = ['', '', '', '']
        this.confirmPin = ['', '', '', '']
        return true
      }

      if (pinValue === this.cardNumber) {
        alert('PIN cannot be similar to the last 4 digits of the card.')
        this.pin = ['', '', '', '']
        this.confirmPin = ['', '', '', '']
        return true
      }

      if (/^(\d)\1+$/.test(pinValue)) {
        alert('PIN cannot be all the same digit.')
        this.pin = ['', '', '', '']
        this.confirmPin = ['', '', '', '']
        return true
      }

      if (!/^\d+$/.test(pinValue)) {
        alert('PIN must contain only numbers.')
        this.pin = ['', '', '', '']
        this.confirmPin = ['', '', '', '']
        return true
      }
      return false
    },
    async changePin(event) {
      event.preventDefault()
      const pinValue = this.pin.join('')
      const confirmPinValue = this.confirmPin.join('')

      const validationErrors = this.validatePin(pinValue, confirmPinValue)
      if (validationErrors) {
        return
      }

      try {
        if (this.cardId && pinValue && confirmPinValue) {
          const response = await this.api.patch(`evedpaycards/${this.cardId}/set-pin`, {
            preventError: true,
            pin: pinValue
          })

          if (response.status === 200) {
            EventBus.$emit('pin-changed')
            this.$router.push({
              path: `/evedpay/details?cardid=${this.cardId}`,
              query: {
                ...this.$route.query,
                ...this.$route.query.cardNumber
              }
            })
          } else {
            this.pin = ['', '', '', '']
            this.confirmPin = ['', '', '', '']
            alert('Error while changing pin.')
          }
        }
      } catch (error) {
        alert('Error while changing pin.')
      }
    }
  }
}
</script>

<style lang='scss' scoped>
  @import "~@/assets/sass/_variables.scss";

  .form .label-format {
    text-align: left !important;
  }

  input {
    width: 55px;
    height: 80px;
    margin-right: 5px;
    margin-left: 5px;
    text-align: center;
    border-radius: 10px;
    border: 2px solid #ccc;
    outline: none;
  }

  .form {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    text-align: center;
    margin-top: 20px;
  }

  .custom-description {
    margin-left: 45px;
    margin-right: 45px;
  }

  .button-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;

    .text-center {
      text-align: center;
    }

    .custom-button {
      background-color: #007bff;
      color: #fff;
      border: 1px solid #007bff;
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 64%;
      margin: 0 auto;
    }

    .custom-button:hover {
      background-color: #007bff;
      color: #fff;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/EvedPay/changePin.vue